import React, { useState, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
	CheckSquare,
	Plus,
	Trash2,
	GripVertical,
	ChevronDown,
	ChevronRight,
	Edit3,
	Save,
	X,
	FileText,
	User,
	Calendar,
	Shield,
	AlertCircle,
} from 'lucide-react'
import checklistService from '@services/checklistService'
import workflowService from '@services/workflowService'

/**
 * Checklists Form Component
 *
 * Full CRUD for checklist items:
 * - View all checklists grouped by certificate
 * - Edit: title, description, control_reference, weightage, evidence, due_date, assigned_to, workflow_status, notes
 * - Add new items
 * - Delete items
 * - Inline editing with expand/collapse
 */
const ChecklistsForm = ({ items, certificates, teamMembers, onChange, disabled }) => {
	const [expandedItems, setExpandedItems] = useState(new Set())
	const [editingItem, setEditingItem] = useState(null)
	const [showAddForm, setShowAddForm] = useState(false)
	const [newItem, setNewItem] = useState(getEmptyItem())
	const [workflows, setWorkflows] = useState([])
	const [loadingWorkflows, setLoadingWorkflows] = useState(false)

	const workflowStatuses = checklistService.getWorkflowStatuses()

	// Fetch available workflows on mount
	useEffect(() => {
		const fetchWorkflows = async () => {
			setLoadingWorkflows(true)
			try {
				const response = await workflowService.getActive()
				setWorkflows(response.data || [])
			} catch (error) {
				console.error('Error fetching workflows:', error)
			} finally {
				setLoadingWorkflows(false)
			}
		}
		fetchWorkflows()
	}, [])

	function getEmptyItem() {
		return {
			title: '',
			description: '',
			control_reference: '',
			weightage: 1,
			evidence_required: '',
			due_date: '',
			assigned_to: null,
			workflow_id: null,
			workflow_name: null,
			workflow_status: 'not_started', // Default status when created
			notes: '',
			certificate_id: certificates[0]?.id || null,
		}
	}

	// Group items by certificate
	const groupedItems = useMemo(() => {
		const groups = {}

		items.forEach((item) => {
			const certId = item.certificate_id || 'uncategorized'
			const certName =
				item.certificate_title ||
				item.certificate_name ||
				certificates.find((c) => c.id === certId)?.name ||
				'General'

			if (!groups[certId]) {
				groups[certId] = {
					name: certName,
					items: [],
				}
			}
			groups[certId].items.push(item)
		})

		return groups
	}, [items, certificates])

	// Toggle item expansion
	const toggleExpand = (itemId) => {
		setExpandedItems((prev) => {
			const next = new Set(prev)
			if (next.has(itemId)) {
				next.delete(itemId)
			} else {
				next.add(itemId)
			}
			return next
		})
	}

	// Handle item field change
	const handleItemChange = (itemId, field, value) => {
		const updatedItems = items.map((item) => {
			if ((item.id && item.id === itemId) || (item._tempId && item._tempId === itemId)) {
				const updated = { ...item, [field]: value }

				// If workflow_id changes, update workflow_name from workflows list
				if (field === 'workflow_id') {
					const selectedWorkflow = workflows.find((w) => w.id === parseInt(value))
					updated.workflow_name = selectedWorkflow ? selectedWorkflow.name : null
				}

				return updated
			}
			return item
		})
		onChange(updatedItems)
	}

	// Delete item
	const handleDelete = (itemId) => {
		if (disabled) return
		if (!window.confirm('Are you sure you want to delete this checklist item?')) return

		const updatedItems = items.filter(
			(item) => item.id !== itemId && item._tempId !== itemId
		)
		onChange(updatedItems)
	}

	// Add new item
	const handleAddItem = () => {
		if (disabled) return
		if (!newItem.title.trim()) {
			alert('Please enter a title for the checklist item.')
			return
		}

		const itemToAdd = {
			...newItem,
			_tempId: `temp_${Date.now()}`,
		}

		onChange([...items, itemToAdd])
		setNewItem(getEmptyItem())
		setShowAddForm(false)
	}

	// Cancel add
	const handleCancelAdd = () => {
		setNewItem(getEmptyItem())
		setShowAddForm(false)
	}

	// Get status badge class
	const getStatusClass = (status) => {
		// Default to 'not_started' if status is null/undefined/empty
		const normalizedStatus = status || 'not_started'
		const statusMap = {
			not_started: 'status--gray',
			in_progress: 'status--blue',
			pending_review: 'status--yellow',
			approved: 'status--green',
			rejected: 'status--red',
			not_applicable: 'status--gray',
		}
		return statusMap[normalizedStatus] || 'status--gray'
	}

	// Get assigned user name
	const getAssignedUserName = (userId) => {
		if (!userId) return 'Unassigned'
		const user = teamMembers.find((m) => m.id === parseInt(userId, 10))
		return user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email : 'Unknown'
	}

	return (
		<div className="checklists-form">
			{/* Header with Add button */}
			<div className="checklists-form__header">
				<div className="checklists-form__header-info">
					<h3 className="form-section__title">
						<CheckSquare size={20} />
						Checklist Items
					</h3>
					<p className="form-section__description">
						Manage compliance checklist items. Click on an item to expand and edit.
					</p>
				</div>
				{!disabled && !showAddForm && (
					<button
						type="button"
						className="checklists-form__add-btn"
						onClick={() => setShowAddForm(true)}
					>
						<Plus size={18} />
						Add Item
					</button>
				)}
			</div>

			{/* Add New Item Form */}
			{showAddForm && (
				<div className="checklist-add-form">
					<h4 className="checklist-add-form__title">
						<Plus size={18} />
						Add New Checklist Item
					</h4>

					<div className="checklist-add-form__grid">
						<div className="form-group form-group--full">
							<label className="form-label required">Title</label>
							<input
								type="text"
								className="form-input"
								value={newItem.title}
								onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
								placeholder="Enter checklist item title"
							/>
						</div>

						<div className="form-group form-group--full">
							<label className="form-label">Description</label>
							<textarea
								className="form-textarea"
								value={newItem.description}
								onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
								placeholder="Detailed description of the requirement"
								rows={3}
							/>
						</div>

						<div className="form-group">
							<label className="form-label">Certificate</label>
							<select
								className="form-select"
								value={newItem.certificate_id || ''}
								onChange={(e) => setNewItem({ ...newItem, certificate_id: e.target.value ? parseInt(e.target.value, 10) : null })}
							>
								<option value="">Select Certificate</option>
								{certificates.map((cert) => (
									<option key={cert.id} value={cert.id}>
										{cert.name || cert.title}
									</option>
								))}
							</select>
						</div>

						<div className="form-group">
							<label className="form-label">Control Reference</label>
							<input
								type="text"
								className="form-input"
								value={newItem.control_reference}
								onChange={(e) => setNewItem({ ...newItem, control_reference: e.target.value })}
								placeholder="e.g., ISO 27001 A.5.1.1"
							/>
						</div>

						<div className="form-group">
							<label className="form-label">Weightage</label>
							<input
								type="number"
								className="form-input"
								value={newItem.weightage}
								onChange={(e) => setNewItem({ ...newItem, weightage: parseInt(e.target.value, 10) || 1 })}
								min={1}
								max={10}
							/>
						</div>

						<div className="form-group">
							<label className="form-label">Workflow</label>
							<select
								className="form-select"
								value={newItem.workflow_id || ''}
								onChange={(e) => {
									const workflowIdStr = e.target.value || null
									const selectedWorkflow = workflowIdStr
										? workflows.find((w) => String(w.id) === workflowIdStr)
										: null
									setNewItem({
										...newItem,
										workflow_id: workflowIdStr ? parseInt(workflowIdStr, 10) : null,
										workflow_name: selectedWorkflow ? selectedWorkflow.name : null,
									})
								}}
								disabled={loadingWorkflows}
							>
								<option value="">{loadingWorkflows ? 'Loading...' : 'Select Workflow'}</option>
								{workflows.map((workflow) => (
									<option key={workflow.id} value={workflow.id}>
										{workflow.name} {workflow.steps_count ? `(${workflow.steps_count} steps)` : ''}
									</option>
								))}
							</select>
						</div>

						<div className="form-group">
							<label className="form-label">Assigned To</label>
							<select
								className="form-select"
								value={newItem.assigned_to || ''}
								onChange={(e) => setNewItem({ ...newItem, assigned_to: e.target.value ? parseInt(e.target.value, 10) : null })}
							>
								<option value="">Unassigned</option>
								{teamMembers.map((member) => (
									<option key={member.id} value={member.id}>
										{member.first_name || ''} {member.last_name || ''} ({member.email})
									</option>
								))}
							</select>
						</div>

						<div className="form-group">
							<label className="form-label">Due Date</label>
							<input
								type="date"
								className="form-input"
								value={newItem.due_date}
								onChange={(e) => setNewItem({ ...newItem, due_date: e.target.value })}
							/>
						</div>

						<div className="form-group form-group--full">
							<label className="form-label">Evidence Required</label>
							<textarea
								className="form-textarea"
								value={newItem.evidence_required}
								onChange={(e) => setNewItem({ ...newItem, evidence_required: e.target.value })}
								placeholder="Describe what evidence is required"
								rows={2}
							/>
						</div>

						<div className="form-group form-group--full">
							<label className="form-label">Notes</label>
							<textarea
								className="form-textarea"
								value={newItem.notes}
								onChange={(e) => setNewItem({ ...newItem, notes: e.target.value })}
								placeholder="Additional notes or comments"
								rows={2}
							/>
						</div>
					</div>

					<div className="checklist-add-form__actions">
						<button type="button" className="btn btn--outline" onClick={handleCancelAdd}>
							<X size={16} />
							Cancel
						</button>
						<button type="button" className="btn btn--primary" onClick={handleAddItem}>
							<Plus size={16} />
							Add Item
						</button>
					</div>
				</div>
			)}

			{/* Grouped Checklists */}
			{Object.keys(groupedItems).length > 0 ? (
				<div className="checklists-form__groups">
					{Object.entries(groupedItems).map(([certId, group]) => (
						<div key={certId} className="checklist-group">
							<div className="checklist-group__header">
								<Shield size={18} />
								<span className="checklist-group__name">{group.name}</span>
								<span className="checklist-group__count">{group.items.length} items</span>
							</div>

							<div className="checklist-group__items">
								{group.items.map((item) => {
									const itemId = item.id || item._tempId
									const isExpanded = expandedItems.has(itemId)

									return (
										<div key={itemId} className={`checklist-item ${isExpanded ? 'expanded' : ''}`}>
											{/* Collapsed Header - Click to expand */}
											{!isExpanded && (
												<div className="checklist-item__header" onClick={() => toggleExpand(itemId)}>
													<button type="button" className="checklist-item__expand">
														<ChevronRight size={18} />
													</button>
													<div className="checklist-item__title">
														{item.title}
														{item._tempId && (
															<span className="checklist-item__new-badge">New</span>
														)}
													</div>
													<span className={`checklist-item__status ${getStatusClass(item.workflow_status)}`}>
														{checklistService.getStatusLabel(item.workflow_status)}
													</span>
													{!disabled && (
														<button
															type="button"
															className="checklist-item__delete"
															onClick={(e) => {
																e.stopPropagation()
																handleDelete(itemId)
															}}
															title="Delete item"
														>
															<Trash2 size={16} />
														</button>
													)}
												</div>
											)}

											{/* Expanded State - Header + Form */}
											{isExpanded && (
												<>
													{/* Prominent Header Card */}
													<div className="checklist-item__header-card">
														<div className="checklist-item__header-card-top">
															<button
																type="button"
																className="checklist-item__collapse-btn"
																onClick={() => toggleExpand(itemId)}
																title="Collapse"
															>
																<ChevronDown size={18} />
															</button>
															<div className="checklist-item__header-info">
																<h4 className="checklist-item__header-title">
																	<CheckSquare size={18} />
																	{item.title || 'Untitled Checklist Item'}
																	{item._tempId && (
																		<span className="checklist-item__new-badge">New</span>
																	)}
																</h4>
																<div className="checklist-item__header-meta">
																	{item.control_reference && (
																		<span className="checklist-item__meta-item">
																			<Shield size={14} />
																			{item.control_reference}
																		</span>
																	)}
																	<span className={`checklist-item__status ${getStatusClass(item.workflow_status)}`}>
																		{checklistService.getStatusLabel(item.workflow_status)}
																	</span>
																	{item.assigned_to && (
																		<span className="checklist-item__meta-item">
																			<User size={14} />
																			{getAssignedUserName(item.assigned_to)}
																		</span>
																	)}
																	{item.due_date && (
																		<span className="checklist-item__meta-item">
																			<Calendar size={14} />
																			{new Date(item.due_date).toLocaleDateString()}
																		</span>
																	)}
																</div>
															</div>
															{!disabled && (
																<button
																	type="button"
																	className="checklist-item__delete"
																	onClick={(e) => {
																		e.stopPropagation()
																		handleDelete(itemId)
																	}}
																	title="Delete item"
																>
																	<Trash2 size={16} />
																</button>
															)}
														</div>
													</div>

													{/* Form Body Below Header */}
													<div className="checklist-item__body">
													<div className="checklist-item__form">
														{/* Basic Information Section */}
														<div className="checklist-item__form-section">
															<div className="checklist-item__form-section-header">
																<FileText size={16} />
																<span>Basic Information</span>
															</div>
															<div className="checklist-item__form-section-content">
																<div className="checklist-item__form-section-grid checklist-item__form-section-grid--2cols">
																	<div className="form-group" style={{ gridColumn: '1 / -1' }}>
																		<label className="form-label">
																			<Edit3 size={14} />
																			Title
																		</label>
																		<input
																			type="text"
																			className="form-input"
																			value={item.title || ''}
																			onChange={(e) => handleItemChange(itemId, 'title', e.target.value)}
																			disabled={disabled}
																			placeholder="Enter checklist item title"
																		/>
																	</div>

																	<div className="form-group" style={{ gridColumn: '1 / -1' }}>
																		<label className="form-label">
																			<FileText size={14} />
																			Description
																		</label>
																		<textarea
																			className="form-textarea"
																			value={item.description || ''}
																			onChange={(e) => handleItemChange(itemId, 'description', e.target.value)}
																			disabled={disabled}
																			rows={3}
																			placeholder="Detailed description of the requirement"
																		/>
																	</div>

																	<div className="form-group">
																		<label className="form-label">
																			<Shield size={14} />
																			Control Reference
																		</label>
																		<input
																			type="text"
																			className="form-input"
																			value={item.control_reference || ''}
																			onChange={(e) => handleItemChange(itemId, 'control_reference', e.target.value)}
																			disabled={disabled}
																			placeholder="e.g., ISO 27001 A.5.1.1"
																		/>
																	</div>

																	<div className="form-group">
																		<label className="form-label">
																			<AlertCircle size={14} />
																			Weightage (1-10)
																		</label>
																		<input
																			type="number"
																			className="form-input"
																			value={item.weightage || 1}
																			onChange={(e) => handleItemChange(itemId, 'weightage', parseInt(e.target.value, 10) || 1)}
																			disabled={disabled}
																			min={1}
																			max={10}
																		/>
																	</div>
																</div>
															</div>
														</div>

														{/* Workflow & Assignment Section */}
														<div className="checklist-item__form-section">
															<div className="checklist-item__form-section-header">
																<User size={16} />
																<span>Workflow & Assignment</span>
															</div>
															<div className="checklist-item__form-section-content">
																<div className="checklist-item__form-section-grid checklist-item__form-section-grid--3cols">
																	<div className="form-group">
																		<label className="form-label">Workflow</label>
																		<select
																			className="form-select"
																			value={item.workflow_id || ''}
																			onChange={(e) => handleItemChange(itemId, 'workflow_id', e.target.value ? parseInt(e.target.value) : null)}
																			disabled={disabled || loadingWorkflows}
																		>
																			<option value="">No workflow assigned</option>
																			{workflows.map((workflow) => (
																				<option key={workflow.id} value={workflow.id}>
																					{workflow.name} ({workflow.steps_count} steps)
																				</option>
																			))}
																		</select>
																		{item.workflow_name && item.workflow_id && (
																			<small className="form-help-text">
																				Currently: {item.workflow_name}
																			</small>
																		)}
																	</div>

																	<div className="form-group">
																		<label className="form-label">Status</label>
																		<select
																			className="form-select"
																			value={item.workflow_status || 'not_started'}
																			onChange={(e) => handleItemChange(itemId, 'workflow_status', e.target.value)}
																			disabled={disabled}
																		>
																			{workflowStatuses.map((status) => (
																				<option key={status.value} value={status.value}>
																					{status.label}
																				</option>
																			))}
																		</select>
																	</div>

																	<div className="form-group">
																		<label className="form-label">
																			<User size={14} />
																			Assigned To
																		</label>
																		<select
																			className="form-select"
																			value={item.assigned_to || ''}
																			onChange={(e) => handleItemChange(itemId, 'assigned_to', e.target.value ? parseInt(e.target.value, 10) : null)}
																			disabled={disabled}
																		>
																			<option value="">Unassigned</option>
																			{teamMembers.map((member) => (
																				<option key={member.id} value={member.id}>
																					{member.first_name || ''} {member.last_name || ''} ({member.email})
																				</option>
																			))}
																		</select>
																	</div>

																	<div className="form-group">
																		<label className="form-label">
																			<Calendar size={14} />
																			Due Date
																		</label>
																		<input
																			type="date"
																			className="form-input"
																			value={item.due_date || ''}
																			onChange={(e) => handleItemChange(itemId, 'due_date', e.target.value)}
																			disabled={disabled}
																		/>
																	</div>
																</div>
															</div>
														</div>

														{/* Evidence & Notes Section */}
														<div className="checklist-item__form-section">
															<div className="checklist-item__form-section-header">
																<FileText size={16} />
																<span>Evidence & Notes</span>
															</div>
															<div className="checklist-item__form-section-content">
																<div className="checklist-item__form-section-grid">
																	<div className="form-group">
																		<label className="form-label">Evidence Required</label>
																		<textarea
																			className="form-textarea"
																			value={item.evidence_required || ''}
																			onChange={(e) => handleItemChange(itemId, 'evidence_required', e.target.value)}
																			disabled={disabled}
																			rows={2}
																			placeholder="Describe what evidence is required for compliance"
																		/>
																	</div>

																	<div className="form-group">
																		<label className="form-label">Notes</label>
																		<textarea
																			className="form-textarea"
																			value={item.notes || ''}
																			onChange={(e) => handleItemChange(itemId, 'notes', e.target.value)}
																			disabled={disabled}
																			rows={2}
																			placeholder="Additional notes or comments"
																		/>
																	</div>
																</div>
															</div>
														</div>
													</div>
												</div>
												</>
											)}
										</div>
									)
								})}
							</div>
						</div>
					))}
				</div>
			) : (
				<div className="checklists-form__empty">
					<CheckSquare size={48} />
					<h4>No Checklist Items</h4>
					<p>This project doesn't have any checklist items yet.</p>
					{!disabled && (
						<button
							type="button"
							className="btn btn--primary"
							onClick={() => setShowAddForm(true)}
						>
							<Plus size={18} />
							Add First Item
						</button>
					)}
				</div>
			)}
		</div>
	)
}

ChecklistsForm.propTypes = {
	items: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			title: PropTypes.string,
			description: PropTypes.string,
			control_reference: PropTypes.string,
			weightage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			evidence_required: PropTypes.string,
			due_date: PropTypes.string,
			assigned_to: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			workflow_status: PropTypes.string,
			notes: PropTypes.string,
			certificate_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		})
	).isRequired,
	certificates: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
			name: PropTypes.string,
			title: PropTypes.string,
		})
	).isRequired,
	teamMembers: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
			first_name: PropTypes.string,
			last_name: PropTypes.string,
			email: PropTypes.string,
		})
	).isRequired,
	onChange: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
}

ChecklistsForm.defaultProps = {
	disabled: false,
}

export default ChecklistsForm
